/**
 * Shared framer-motion presets.
 *
 * Every preset animates *only* opacity / transform so nothing here can change
 * document layout, image sources or spacing — it just changes how the existing
 * markup arrives on screen.
 */

// easeOutQuint-ish: quick start, long settle. Reads as "expensive" rather than bouncy.
export const EASE_OUT = [0.22, 1, 0.36, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];

export const DURATION = {
  fast: 0.25,
  base: 0.5,
  slow: 0.75,
};

export const transitions = {
  base: { duration: DURATION.base, ease: EASE_OUT },
  fast: { duration: DURATION.fast, ease: EASE_OUT },
  slow: { duration: DURATION.slow, ease: EASE_OUT },
  // Used by shared-layout elements (the booking tab highlight).
  spring: { type: "spring", stiffness: 420, damping: 38, mass: 0.9 },
};

/** Fade + rise. `y` is overridable per call site. */
export const fadeUp = (y = 24, duration = DURATION.base) => ({
  hidden: { opacity: 0, y },
  visible: { opacity: 1, y: 0, transition: { duration, ease: EASE_OUT } },
});

export const fadeIn = (duration = DURATION.base) => ({
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration, ease: EASE_OUT } },
});

export const fadeSide = (x = 24, duration = DURATION.base) => ({
  hidden: { opacity: 0, x },
  visible: { opacity: 1, x: 0, transition: { duration, ease: EASE_OUT } },
});

/** Parent that times its children. Children read `hidden`/`visible` by inheritance. */
export const staggerContainer = (stagger = 0.09, delayChildren = 0) => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

/** Rule drawn from the left — the divider under the hero-banner copy. */
export const drawLine = (duration = DURATION.slow) => ({
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: { duration, ease: EASE_OUT },
  },
});

/** SVG stroke that draws itself (the WELL Cabin hotspot leader line). */
export const drawStroke = (duration = 0.7, delay = 0) => ({
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration, ease: EASE_IN_OUT, delay },
      opacity: { duration: 0.2, delay },
    },
  },
});

/**
 * Card hover. Applied on the card as `whileHover="hover"`; the image wrapper
 * inside it picks up `imageZoom` through framer's variant propagation, so one
 * gesture drives both the lift and the zoom without any CSS group-hover.
 */
export const cardHover = (lift = -6) => ({
  rest: { y: 0 },
  hover: { y: lift, transition: { duration: DURATION.fast, ease: EASE_OUT } },
});

export const imageZoom = (scale = 1.06) => ({
  rest: { scale: 1 },
  hover: { scale, transition: { duration: DURATION.slow, ease: EASE_OUT } },
});

export const pressable = {
  hover: { scale: 1.015, transition: transitions.fast },
  tap: { scale: 0.985, transition: { duration: 0.12 } },
};

/** Default viewport config: animate once, when a fifth of the block is in view. */
export const VIEWPORT = { once: true, amount: 0.2, margin: "0px 0px -80px 0px" };

/**
 * Strips movement out of any variant set when the user asks for reduced motion,
 * keeping the opacity fade so nothing ever pops in without warning.
 */
export const respectReducedMotion = (variants, shouldReduce) => {
  if (!shouldReduce) return variants;
  return {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: DURATION.fast } },
    rest: {},
    hover: {},
    tap: {},
  };
};
